import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';

import Admin from './components/Admin';
import AdminMFA from './components/admin/auth/AdminMFA';
import ProtectedRoute from './components/admin/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import Home from './components/Home';
import ProtectedMemberRoute from './components/member/auth/ProtectedMemberRoute';
import MagazineViewer from './components/member/MagazineViewer';
import ManageOthers from './components/member/ManageOthers';
import MemberDashboard from './components/member/MemberDashboard';
import MemberMagazine from './components/member/MemberMagazine';
import PaymentHistory from './components/member/PaymentHistory';
import Profile from './components/member/Profile';
import Membership from './components/Membership';
import { RegistrationProvider } from './components/membership/RegistrationContext';
import NotFound from './components/NotFound';
import Renewal from './components/Renewal';
import { AdminProvider } from './contexts/AdminContext';
import { AuthProvider } from './contexts/AuthContext';
import { MemberProvider } from './contexts/MemberContext';

// Wrapper to determine which routes should use minimal layout
function RouteWrapper({ children }) {
  const location = useLocation();
  const isMinimalLayout =
    location.pathname === '/login' ||
    location.pathname === '/admin/mfa' ||
    location.pathname.startsWith('/admin');

  // If it's a minimal layout route, render children directly
  if (isMinimalLayout) {
    return children;
  }

  // Otherwise, render children (they will use PublicLayout or AuthenticatedLayout as appropriate)
  return children;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <MemberProvider>
          <RouteWrapper>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />

              {/* Protected Member Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedMemberRoute>
                    <MemberDashboard />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedMemberRoute>
                    <Profile />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/magazine"
                element={
                  <ProtectedMemberRoute>
                    <MemberMagazine />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/magazine/:id"
                element={
                  <ProtectedMemberRoute>
                    <MagazineViewer />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/documents"
                element={
                  <ProtectedMemberRoute>
                    <MemberDashboard />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/events"
                element={
                  <ProtectedMemberRoute>
                    <MemberDashboard />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedMemberRoute>
                    <PaymentHistory />
                  </ProtectedMemberRoute>
                }
              />
              <Route
                path="/manage-others"
                element={
                  <ProtectedMemberRoute>
                    <ManageOthers />
                  </ProtectedMemberRoute>
                }
              />

              {/* Public Routes */}
              <Route
                path="/membership/*"
                element={
                  <RegistrationProvider>
                    <Membership />
                  </RegistrationProvider>
                }
              />
              <Route
                path="/renewal"
                element={
                  <RegistrationProvider>
                    <Renewal />
                  </RegistrationProvider>
                }
              />

              {/* Admin Routes */}
              <Route path="/admin/mfa" element={<AdminMFA />} />
              <Route
                path="/admin/*"
                element={
                  <ProtectedRoute>
                    <AdminProvider>
                      <Admin />
                    </AdminProvider>
                  </ProtectedRoute>
                }
              />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </RouteWrapper>
        </MemberProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
